import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { FormattedMessage } from 'react-intl';

import AnalyseApp from 'AnalyseApp';
import {
  ROUTE_LOGIN,
  ROUTE_SIGNUP,
  ROUTE_FORGOT,
  ROUTE_DISCOVER,
  ROUTE_TIPS,
  ROUTE_ANALYSE,
  ROUTE_USERS,
  ROUTE_PROFILE,
  ROUTE_SET_PASSWORD,
  ROUTE_DISCOVER_RESULT_PDF
} from 'RouteList';

import Login from 'containers/login/Login';
import SignupView from 'containers/login/SignupView';
import SetPasswordView from './containers/login/SetPasswordView';
import ForgotLoginView from 'containers/login/ForgotLoginView';
import PrivateRoute from 'containers/PrivateRoute';
import LoginRoute from 'containers/LoginRoute';
import Users from 'containers/users/Users';
import Alert from 'components/Alert/Alert';
import LoaderOverlay from 'components/LoaderOverlay/LoaderOverlay';
import Drawer from 'components/Drawer/Drawer';
import DrawerHeader from 'components/Drawer/DrawerHeader';
import Navigation from 'components/Navigation/Navigation';
import NavigationItem from 'components/Navigation/NavigationItem';
import NavigationLogoutWrapper from 'components/Navigation/NavigationLogout';

import { drawerItems } from 'DrawerList';
import Home from 'containers/static/Home';
import Discover from 'containers/discover/Discover';
import Tips from 'containers/tips/Tips';
import Profile from 'containers/profile/Profile';

import Icon from 'components/Icon/Icon';
import styled from 'styled-components';
import { Flex } from 'components/Layout/Layout';
import ResultPDFView from './containers/discover/ResultPDFView';
import { useStores } from './store/useStores';

const LogoWrapper = styled.div`
  position: relative;
  text-align: center;
  display: block;
  margin-top: 20px;

  svg {
    width: 80%;
    max-height: 50px;
  }
`;

const ContactGroup = styled.ul`
  list-style: none;
  font-family: ${props => props.theme.fonts.fontface};
  text-transform: uppercase;
  font-size: ${props => props.theme.fonts.sizeSmall};
  padding: 0 10px 0 30px;

  li {
    padding: 0;
  }
`;

const ContactWrapper = styled.div`
  background: white;
  padding: 30px 0 0;
  align-self: flex-end;
`;

function AppRoute() {
  const location = useLocation();
  const { authStore, routing, uiState } = useStores();
  const { isLoggedIn, user, tmpCognitoUser, isImpersonator } = authStore;
  const { appIsInSync, isMenuOpen, alertModel } = uiState;
  const { push } = routing;

  const currentUser = isImpersonator || user;

  const drawerItemsFiltered =
    currentUser && !currentUser.admin
      ? drawerItems.filter(item => !item.admin)
      : drawerItems;

  const showDrawer = location.pathname !== ROUTE_DISCOVER_RESULT_PDF;

  const logout = useCallback(() => {
    if (authStore.isImpersonator) {
      authStore.unsetImpersonator();
      push(ROUTE_USERS);
    } else {
      authStore.logout();
    }
  }, [authStore, push]);

  return (
    <React.Fragment>
      {!appIsInSync && <LoaderOverlay white />}
      {alertModel && <Alert {...alertModel} />}
      {isLoggedIn && showDrawer && (
        <Drawer show={isMenuOpen} onClick={() => uiState.hideMenu()}>
          <div>
            <DrawerHeader
              firstname={currentUser && currentUser.firstname}
              lastname={currentUser && currentUser.lastname}
            />
            <Navigation>
              {drawerItemsFiltered.map(item => (
                <NavigationItem
                  key={item.route}
                  active={
                    `${routing.location.pathname.split('/')[1]}` ===
                    `${item.route.split('/')[1]}`
                  }
                  icon={item.icon}
                  onClick={() => {
                    push(item.route);
                  }}
                >
                  <FormattedMessage {...item.label} />
                </NavigationItem>
              ))}
            </Navigation>
          </div>
          <Flex flexDirection="column" justifyContent="flex-end">
            <ContactWrapper>
              <LogoWrapper>
                <Icon id="ugent-ge" fillColor="#e85e71" />
              </LogoWrapper>

              <ContactGroup>
                <li>Vakgroep bewegings- en sportwetenschappen</li>
                <li>Onderzoeksgroep sport pedagogiek</li>
                <li>
                  Contactpersoon
                  <ul>
                    <li>Prof. L. Haerens</li>
                  </ul>
                </li>
                <li>
                  Ontwikkeld met steun van het Fonds Voor Wetenschappelijk
                  Onderzoek
                </li>
              </ContactGroup>
            </ContactWrapper>

            <NavigationLogoutWrapper logout={logout} />
          </Flex>
        </Drawer>
      )}

      <Routes>
        {/* Public (only when NOT logged in) */}
        <Route element={<LoginRoute />}>
          <Route path={ROUTE_LOGIN} element={<Login />} />
          <Route path={ROUTE_SIGNUP} element={<SignupView />} />
          <Route path={ROUTE_FORGOT} element={<ForgotLoginView />} />
          {tmpCognitoUser && (
            <Route path={ROUTE_SET_PASSWORD} element={<SetPasswordView />} />
          )}
        </Route>

        {/* Private area */}
        <Route element={<PrivateRoute />}>
          <Route path={ROUTE_DISCOVER_RESULT_PDF} element={<ResultPDFView />} />
          <Route path={`${ROUTE_DISCOVER}/*`} element={<Discover />} />

          {/* Analyse subtree */}
          <Route path={`${ROUTE_ANALYSE}/*`} element={<AnalyseApp />} />

          <Route path={ROUTE_TIPS} element={<Tips />} />
          <Route path={ROUTE_PROFILE} element={<Profile />} />
          {currentUser && currentUser.admin && (
            <Route path={ROUTE_USERS} element={<Users />} />
          )}
          <Route path="/" element={<Home />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Fragment>
  );
}

export default observer(AppRoute);
